import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { accountingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { BookOpen, Plus, Trash2, Download, FileCheck2, Ban, Loader2, Scale, Lock } from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const VOUCHER_TYPES = [['JV', 'Journal Voucher'], ['PV', 'Payment Voucher'], ['RV', 'Receipt Voucher'], ['AJ', 'Adjustment']];
const STATUS_STYLE = { DRAFT: 'bg-amber-100 text-amber-700', POSTED: 'bg-emerald-100 text-emerald-700', VOID: 'bg-red-100 text-red-600', CL: 'bg-slate-100' };

export default function AccountingPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [tab, setTab] = useState('vouchers');
  const [accounts, setAccounts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [tb, setTb] = useState(null);
  const [is, setIs] = useState(null);
  const [bs, setBs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voucherDialog, setVoucherDialog] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    try {
      const [a, v, p, t, i, b] = await Promise.all([
        accountingApi.listAccounts(pid), accountingApi.listVouchers(pid), accountingApi.listPeriods(pid),
        accountingApi.trialBalance(pid), accountingApi.incomeStatement(pid), accountingApi.balanceSheet(pid),
      ]);
      setAccounts(a.data || []); setVouchers(v.data || []); setPeriods(p.data || []);
      setTb(t.data); setIs(i.data); setBs(b.data);
    } catch (e) {
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const doSeed = async () => {
    try { const r = await accountingApi.seedCoa(pid); toast.success(`Seeded ${r.data.created} accounts`); load(); }
    catch (e) { toast.error('Failed to seed'); }
  };
  const post = async (id) => { try { await accountingApi.postVoucher(id); toast.success('Posted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Post failed'); } };
  const voidV = async (id) => { try { await accountingApi.voidVoucher(id); toast.success('Voided'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Void failed'); } };

  const exportJournals = async () => {
    try {
      const token = localStorage.getItem('hms_token');
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/accounting/export/journals?property_id=${pid}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = 'journals_peachtree.csv';
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Exported journals (Peachtree CSV)');
    } catch (e) { toast.error('Export failed'); }
  };

  if (!currentProperty) return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-800 text-white flex items-center justify-center"><BookOpen className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-bold text-slate-900">Accounting</h1><p className="text-sm text-slate-500">General ledger, vouchers, financial statements</p></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportJournals}><Download className="h-4 w-4 mr-2" /> Peachtree CSV</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="vouchers">Vouchers</TabsTrigger>
          <TabsTrigger value="accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="trial">Trial Balance</TabsTrigger>
          <TabsTrigger value="statements">Financial Statements</TabsTrigger>
          <TabsTrigger value="periods">Periods</TabsTrigger>
        </TabsList>

        {/* VOUCHERS */}
        <TabsContent value="vouchers" className="space-y-4">
          <div className="flex justify-end"><Button onClick={() => setVoucherDialog(true)}><Plus className="h-4 w-4 mr-2" /> New Voucher</Button></div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Voucher</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  : vouchers.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No vouchers yet.</TableCell></TableRow>
                  : vouchers.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-mono text-xs">{v.voucher_number}</TableCell>
                      <TableCell>{v.voucher_type}</TableCell>
                      <TableCell>{v.entry_date}</TableCell>
                      <TableCell className="max-w-[260px] truncate">{v.description}</TableCell>
                      <TableCell className="text-right">{ETB(v.total_debit)}</TableCell>
                      <TableCell><Badge className={STATUS_STYLE[v.status] || ''}>{v.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        {v.status === 'DRAFT' && <Button variant="ghost" size="icon" onClick={() => post(v.id)} title="Post"><FileCheck2 className="h-4 w-4 text-emerald-600" /></Button>}
                        {v.status === 'POSTED' && <Button variant="ghost" size="icon" onClick={() => voidV(v.id)} title="Void"><Ban className="h-4 w-4 text-red-500" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* CHART OF ACCOUNTS */}
        <TabsContent value="accounts" className="space-y-4">
          {accounts.length === 0 && (
            <Card><CardContent className="p-6 text-center"><p className="text-slate-500 mb-3">No chart of accounts yet.</p><Button onClick={doSeed}>Seed Default Chart of Accounts</Button></CardContent></Card>
          )}
          {accounts.length > 0 && (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Normal</TableHead></TableRow></TableHeader>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a.id}><TableCell className="font-mono">{a.code}</TableCell><TableCell>{a.name}</TableCell><TableCell><Badge variant="outline">{a.account_type}</Badge></TableCell><TableCell className="text-xs text-slate-500">{a.normal_balance}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* TRIAL BALANCE */}
        <TabsContent value="trial" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4" /> Trial Balance</CardTitle>
              <CardDescription>{tb?.balanced ? <span className="text-emerald-600">Balanced</span> : <span className="text-red-600">Out of balance</span>}</CardDescription></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(tb?.rows || []).map((r) => (
                    <TableRow key={r.code}><TableCell className="font-mono">{r.code}</TableCell><TableCell>{r.name}</TableCell><TableCell className="text-right">{r.debit ? ETB(r.debit) : '—'}</TableCell><TableCell className="text-right">{r.credit ? ETB(r.credit) : '—'}</TableCell></TableRow>
                  ))}
                  <TableRow className="font-semibold border-t-2"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right">{ETB(tb?.total_debit)}</TableCell><TableCell className="text-right">{ETB(tb?.total_credit)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINANCIAL STATEMENTS */}
        <TabsContent value="statements" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Income Statement</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                <p className="font-semibold text-slate-500 mt-1">Income</p>
                {(is?.income || []).map((r) => <div key={r.code} className="flex justify-between"><span>{r.name}</span><span>{ETB(r.amount)}</span></div>)}
                <div className="flex justify-between font-medium border-t pt-1"><span>Total Income</span><span>{ETB(is?.total_income)}</span></div>
                <p className="font-semibold text-slate-500 mt-2">Expenses</p>
                {(is?.expense || []).map((r) => <div key={r.code} className="flex justify-between"><span>{r.name}</span><span>{ETB(r.amount)}</span></div>)}
                <div className="flex justify-between font-medium border-t pt-1"><span>Total Expense</span><span>{ETB(is?.total_expense)}</span></div>
                <div className="flex justify-between font-bold border-t-2 pt-1 mt-1 text-emerald-700"><span>Net Income</span><span>{ETB(is?.net_income)}</span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Balance Sheet</CardTitle>
                <CardDescription>{bs?.balanced ? <span className="text-emerald-600">Balanced</span> : <span className="text-red-600">Out of balance</span>}</CardDescription></CardHeader>
              <CardContent className="space-y-1 text-sm">
                <p className="font-semibold text-slate-500">Assets</p>
                {(bs?.assets || []).map((r) => <div key={r.code} className="flex justify-between"><span>{r.name}</span><span>{ETB(r.amount)}</span></div>)}
                <div className="flex justify-between font-medium border-t pt-1"><span>Total Assets</span><span>{ETB(bs?.total_assets)}</span></div>
                <p className="font-semibold text-slate-500 mt-2">Liabilities</p>
                {(bs?.liabilities || []).map((r) => <div key={r.code} className="flex justify-between"><span>{r.name}</span><span>{ETB(r.amount)}</span></div>)}
                <p className="font-semibold text-slate-500 mt-2">Equity</p>
                {(bs?.equity || []).map((r) => <div key={r.code} className="flex justify-between"><span>{r.name}</span><span>{ETB(r.amount)}</span></div>)}
                <div className="flex justify-between font-bold border-t-2 pt-1 mt-1"><span>Liabilities + Equity</span><span>{ETB((bs?.total_liabilities || 0) + (bs?.total_equity || 0))}</span></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PERIODS */}
        <TabsContent value="periods" className="space-y-4">
          <PeriodsPanel pid={pid} periods={periods} onChange={load} />
        </TabsContent>
      </Tabs>

      <VoucherDialog open={voucherDialog} onOpenChange={setVoucherDialog} pid={pid} accounts={accounts} onSaved={() => { setVoucherDialog(false); load(); }} />
    </div>
  );
}

function PeriodsPanel({ pid, periods, onChange }) {
  const [name, setName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const add = async () => {
    if (!name || !start || !end) { toast.error('Fill all period fields'); return; }
    try { await accountingApi.createPeriod(pid, { name, fiscal_year: parseInt(year), start_date: start, end_date: end }); toast.success('Period created'); setName(''); setStart(''); setEnd(''); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const close = async (id) => { try { await accountingApi.closePeriod(id); toast.success('Period closed'); onChange(); } catch (e) { toast.error('Failed'); } };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Fiscal Periods</CardTitle><CardDescription>Closed periods block further postings on those dates.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jan 2026" /></div>
          <div><Label className="text-xs">Year</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} /></div>
          <div><Label className="text-xs">Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label className="text-xs">End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <Button onClick={add}><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Year</TableHead><TableHead>Range</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {periods.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No periods defined.</TableCell></TableRow>
              : periods.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell><TableCell>{p.fiscal_year}</TableCell><TableCell className="text-sm">{p.start_date} → {p.end_date}</TableCell>
                  <TableCell><Badge className={p.status === 'CLOSED' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right">{p.status === 'OPEN' && <Button variant="ghost" size="sm" onClick={() => close(p.id)}><Lock className="h-4 w-4 mr-1" /> Close</Button>}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function VoucherDialog({ open, onOpenChange, pid, accounts, onSaved }) {
  const [type, setType] = useState('JV');
  const [entryDate, setEntryDate] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([{ account_id: '', debit: '', credit: '' }, { account_id: '', debit: '', credit: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setType('JV'); setEntryDate(new Date().toISOString().slice(0, 10)); setDescription(''); setLines([{ account_id: '', debit: '', credit: '' }, { account_id: '', debit: '', credit: '' }]); }
  }, [open]);

  const totDr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totCr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totDr - totCr) < 0.01 && totDr > 0;

  const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines([...lines, { account_id: '', debit: '', credit: '' }]);
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async (doPost) => {
    const validLines = lines.filter((l) => l.account_id && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0));
    if (validLines.length < 2) { toast.error('Add at least two lines'); return; }
    if (!balanced) { toast.error('Voucher must be balanced (debit = credit)'); return; }
    try {
      setSaving(true);
      await accountingApi.createVoucher(pid, {
        voucher_type: type, entry_date: entryDate, description,
        post: doPost,
        lines: validLines.map((l) => ({ account_id: l.account_id, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })),
      });
      toast.success(doPost ? 'Voucher posted' : 'Voucher saved as draft');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save voucher');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Voucher</DialogTitle><DialogDescription>Double-entry — debits must equal credits.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Type</Label><Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{VOUCHER_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Date</Label><Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} /></div>
            <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Lines</Label><Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button></div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6">
                  <Select value={l.account_id} onValueChange={(v) => setLine(i, 'account_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Account" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Input type="number" placeholder="Debit" value={l.debit} onChange={(e) => setLine(i, 'debit', e.target.value)} /></div>
                <div className="col-span-2"><Input type="number" placeholder="Credit" value={l.credit} onChange={(e) => setLine(i, 'credit', e.target.value)} /></div>
                <div className="col-span-2">{lines.length > 2 && <Button type="button" variant="ghost" size="icon" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}</div>
              </div>
            ))}
          </div>
          <div className={`rounded-lg p-3 flex justify-between text-sm ${balanced ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <span>Total Debit: <b>{ETB(totDr)}</b></span>
            <span>Total Credit: <b>{ETB(totCr)}</b></span>
            <span className={balanced ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>{balanced ? 'Balanced ✓' : 'Not balanced'}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={saving}>Save Draft</Button>
          <Button onClick={() => submit(true)} disabled={saving || !balanced}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Post</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
